import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { AudioRecordingPanel } from '@/components/epd/AudioRecordingPanel';
import { SoepReviewScreen, SoepPayload } from '@/components/epd/SoepReviewScreen';
import { OnboardingWizard, OnboardingSettings } from '@/components/epd/OnboardingWizard';
import { AZURE_SPEECH_LANGUAGES } from '@/lib/azure-languages';
import { toast } from '@/hooks/use-toast';
import { api } from '@/lib/api';
import { Loader2, ShieldX, KeyRound } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useConsultationCosts } from '@/hooks/useConsultationCosts';
import { usePracticeSession } from '@/hooks/usePracticeSession';

/**
 * Het opnamescherm, geopend vanuit het HIS met ?token=...&app_token=...
 *
 * De veertig regels `??`-ketens die hier stonden om praktijk- en
 * gebruikersinstellingen samen te voegen zijn naar usePracticeSession verhuisd,
 * samen met de tokenruil. Wat er verder verdween: het doorgeven van `token`,
 * `user_id` en `app_token` aan elk kindcomponent en aan elke vervolgpagina. De
 * sessie zit in een HttpOnly-cookie, dus die tokens hoeven niet meer door de
 * applicatie te reizen -- en niet meer in de browsergeschiedenis te staan.
 */
export default function SoepPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessie = usePracticeSession(searchParams);
  const consultationCosts = useConsultationCosts(sessie.status.fase === 'ok');

  const [pendingPayload, setPendingPayload] = useState<SoepPayload | null>(null);
  const [manualAppToken, setManualAppToken] = useState('');
  const [appTokenFout, setAppTokenFout] = useState('');
  const [bezigMetAppToken, setBezigMetAppToken] = useState(false);

  const handleOnboardingComplete = async (instellingen: OnboardingSettings) => {
    if (!sessie.practiceUserId) {
      sessie.markeerOnboardingKlaar();
      return;
    }

    try {
      // De praktijknaam gaat via een eigen route. In de bron kon update-practice-user
      // die wijzigen, wat er niet bij hoorde -- zie docs/autorisatiematrix.md.
      //
      // De wizard legt de naam zelf al vast voordat hij de verificatiemail aanvraagt,
      // want het addendum in die mail leest hem uit de database. Deze PATCH blijft
      // staan voor de weg zonder verificatiemail en voor een naam die daarna nog is
      // gewijzigd; dezelfde waarde nog eens zetten kan geen kwaad.
      if (sessie.practiceNeedsName && instellingen.practice_name) {
        await api.patch('/practice', { practiceName: instellingen.practice_name });
        sessie.markeerPraktijkNaam(instellingen.practice_name);
      }

      await api.patch('/me', {
        displayName: instellingen.display_name,
        providerRole: instellingen.provider_role || null,
        providerSpecializations: instellingen.provider_specializations,
        reportVerbosity: instellingen.report_verbosity,
        doctorPhrase: instellingen.doctor_phrase,
        readTriggerPhrase: instellingen.read_trigger_phrase,
        personalProfile: instellingen.personal_profile,
      });

      sessie.pasSettingsAan({
        doctor_phrase: instellingen.doctor_phrase,
        read_trigger_phrase: instellingen.read_trigger_phrase,
        provider_role: instellingen.provider_role,
        provider_specializations: instellingen.provider_specializations,
        report_verbosity: instellingen.report_verbosity,
        personal_profile: instellingen.personal_profile,
      });

      toast({
        title: 'Profiel opgeslagen',
        description:
          'Uw instellingen zijn opgeslagen. U kunt deze later wijzigen via het tandwiel-icoon.',
      });
    } catch {
      toast({
        title: 'Fout',
        description: 'Instellingen konden niet worden opgeslagen.',
        variant: 'destructive',
      });
    }

    sessie.markeerOnboardingKlaar();
  };

  const selectedLanguages = sessie.settings.default_languages
    .map((code) => AZURE_SPEECH_LANGUAGES.find((l) => l.code === code))
    .filter(Boolean) as typeof AZURE_SPEECH_LANGUAGES;

  const sendPayload = (payload: SoepPayload) => {
    const wb = (window as unknown as { webBridge?: { isAvailable: () => boolean; emit: (k: string, v: unknown) => void } }).webBridge;

    if (wb && wb.isAvailable()) {
      wb.emit('soep', payload);
      toast({
        title: 'SOEP verzonden',
        description: `${payload.problems.length} deelcontact(en) verstuurd via WebBridge naar MicroHIS`,
      });
    } else {
      const localApiUrl = sessie.settings.local_api_url || 'http://localhost:8080/soep';
      fetch(localApiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          toast({
            title: 'SOEP verzonden',
            description: `${payload.problems.length} deelcontact(en) gepost naar ${localApiUrl}`,
          });
        })
        .catch(() => {
          toast({
            title: 'SOEP gegenereerd',
            description: 'Kon niet verbinden met lokale API. SOEP is wel gegenereerd.',
          });
        });
    }
    setPendingPayload(null);
  };

  const handleSoepGenerated = (
    soep: { subjective: string; objective: string; evaluation: string; plan: string },
    transcript: string,
    problems?: Array<{
      title: string;
      subjective: string;
      objective: string;
      evaluation: string;
      plan: string;
    }>,
  ) => {
    const soepProblems =
      problems && problems.length > 0
        ? problems
        : [
            {
              title: '',
              subjective: soep.subjective || '',
              objective: soep.objective || '',
              evaluation: soep.evaluation || '',
              plan: soep.plan || '',
            },
          ];

    setPendingPayload({
      consultation_date: new Date().toISOString(),
      problems: soepProblems.map((p) => ({
        title: p.title || null,
        subjective: p.subjective || null,
        objective: p.objective || null,
        evaluation: p.evaluation || null,
        plan: p.plan || null,
        icpc_code: null,
        icpc_description: null,
      })),
      subjective: soepProblems[0]?.subjective || null,
      objective: soepProblems[0]?.objective || null,
      evaluation: soepProblems[0]?.evaluation || null,
      plan: soepProblems[0]?.plan || null,
      transcript: transcript || null,
    });
  };

  // --- Toestanden vóór het scherm zelf -----------------------------------

  if (sessie.status.fase === 'bezig') {
    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-3">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mx-auto" />
          <p className="text-sm text-muted-foreground">Toegang controleren...</p>
        </div>
      </div>
    );
  }

  if (sessie.status.fase === 'app-token-nodig') {
    const verstuurAppToken = async () => {
      const opgeschoond = manualAppToken.trim();
      if (!opgeschoond) {
        setAppTokenFout('Voer een geldige App Token in.');
        return;
      }

      setBezigMetAppToken(true);
      setAppTokenFout('');
      await sessie.probeerAppToken(opgeschoond);
      setBezigMetAppToken(false);
      // Lukt het niet, dan zet de hook de status op geen-toegang; dat is hier de
      // enige manier waarop we nog in dit scherm staan.
      setAppTokenFout('Ongeldige App Token. Probeer opnieuw.');
    };

    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <div className="w-full max-w-sm space-y-4 p-6">
          <div className="text-center space-y-2">
            <KeyRound className="h-10 w-10 text-primary mx-auto" />
            <h2 className="text-lg font-semibold">App Token vereist</h2>
            <p className="text-sm text-muted-foreground">
              Voer de App Token in om toegang te krijgen tot het opnamescherm.
            </p>
          </div>
          <Input
            type="password"
            placeholder="App Token"
            value={manualAppToken}
            onChange={(e) => {
              setManualAppToken(e.target.value);
              setAppTokenFout('');
            }}
            onKeyDown={(e) => e.key === 'Enter' && void verstuurAppToken()}
          />
          {appTokenFout && <p className="text-sm text-destructive">{appTokenFout}</p>}
          <Button className="w-full" onClick={() => void verstuurAppToken()} disabled={bezigMetAppToken}>
            {bezigMetAppToken ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Doorgaan
          </Button>
        </div>
      </div>
    );
  }

  if (sessie.status.fase === 'geblokkeerd') {
    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-3">
          <ShieldX className="h-12 w-12 text-destructive mx-auto" />
          <h2 className="text-lg font-semibold">Geen toegang</h2>
          <p className="text-sm text-muted-foreground max-w-sm">{sessie.status.melding}</p>
        </div>
      </div>
    );
  }

  if (sessie.status.fase === 'geen-toegang') {
    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-3">
          <ShieldX className="h-12 w-12 text-destructive mx-auto" />
          <h2 className="text-lg font-semibold">Geen toegang</h2>
          <p className="text-sm text-muted-foreground max-w-sm">
            {sessie.status.melding} Voeg een{' '}
            <code className="bg-muted px-1 rounded">?token=...</code> parameter toe aan de URL.
          </p>
        </div>
      </div>
    );
  }

  if (sessie.needsOnboarding) {
    return (
      <OnboardingWizard
        onComplete={handleOnboardingComplete}
        userName=""
        practiceNeedsName={sessie.practiceNeedsName}
        practiceUserId={sessie.practiceUserId || undefined}
      />
    );
  }

  if (pendingPayload) {
    return (
      <SoepReviewScreen
        payload={pendingPayload}
        onSend={sendPayload}
        onCancel={() => setPendingPayload(null)}
        onDelete={() => {
          setPendingPayload(null);
          toast({
            title: 'SOEP verwijderd',
            description: 'Het concept is verwijderd en niet verzonden.',
          });
        }}
        autoSuggestIcpc={sessie.settings.auto_suggest_icpc}
        practiceSettings={sessie.settings}
        practiceName={sessie.practice?.practiceName ?? ''}
        consultationCosts={consultationCosts.costs}
      />
    );
  }

  return (
    <div className="h-screen bg-background">
      <AudioRecordingPanel
        onSoepGenerated={handleSoepGenerated}
        onClose={() => {}}
        languages={selectedLanguages.map((l) => l.code)}
        practiceSettings={sessie.settings}
        // Geen tokens meer in de URL: de sessie zit in het cookie, dus /settings en
        // /handleiding hebben niets nodig om zichzelf te kunnen openen.
        onOpenSettings={sessie.practiceUserId ? () => navigate('/settings') : undefined}
        onOpenManual={() => navigate('/handleiding')}
        practiceUserId={sessie.practiceUserId}
        consultationCosts={consultationCosts}
      />
    </div>
  );
}
